/**
 * @author Mohamadhassan Ebrahimi
 *
 * An extension to `Set` that can compare items by custom compare function.
 */
export class GeneralSet<T extends Object> extends Set<T> {
    private compareFn = <X extends T>(t1: X, t2: X) => t1 === t2;

    private prioritize = <X extends T>(existingValue: X, _currentValue: X) =>
        existingValue;

    private getPriority(existing: T, current: T) {
        const keep = this.prioritize(existing, current);
        const remove = keep === existing ? current : existing;
        return { keep, remove };
    }

    private readonly distinct = () => {
        if (this.size < 2) {
            return;
        }
        const cloneArray = this.clone().toArray();
        for (let i = 0; i < cloneArray.length; i++) {
            const outerElement = cloneArray[i];
            for (let j = i + 1; j < cloneArray.length; j++) {
                const innerElement = cloneArray[j];
                if (this.compareFn(outerElement, innerElement)) {
                    const { remove } = this.getPriority(
                        outerElement,
                        innerElement
                    );
                    super.delete(remove);
                }
            }
        }
    };

    /**
     * sets the compare function
     *
     * if not set, compares with `===` simply
     * @param compare
     * @returns the set itself, for FP purpose
     */
    setCompareFn: <X extends T>(compare: (t1: X, t2: X) => boolean) => this =
        compare => {
            this.compareFn = compare as any; // temp fix
            this.distinct();
            return this;
        };

    /**
     * sets the priority function
     *
     * if not set, simply keeps the existing element
     * @param prioritize
     * @returns the set itself, for FP purpose
     */
    setPrioritizeFn: <X extends T>(
        prioritize: (existing: X, current: X) => X
    ) => this = prioritize => {
        this.prioritize = (a, b) => {
            const result = prioritize(a as any, b as any) as any;
            if (result !== a && result !== b) {
                throw new Error(
                    'Prioritize function must return either a or b, not a whole new value'
                );
            }
            return result;
        };
        return this;
    };

    setPriorityOrder: (order: 'exist' | 'new') => this = order =>
        this.setPrioritizeFn((existingValue, currentValue) =>
            order === 'exist' ? existingValue : currentValue
        );

    /**
     * sets the compare key, the unique identifier for T
     *
     * equals to set compareFn with `this.compareKey === that.compareKey`
     * @param compareKey
     * @returns the set itself, for FP purpose
     */
    setCompareKey = (compareKey: keyof T) =>
        this.setCompareFn(
            ({ [compareKey]: k1 }, { [compareKey]: k2 }) => k1 === k2
        );

    /**
     * adds the item to this set (if not present)
     * @param value
     * @returns the set itself, for FP purpose
     */
    override add: <X extends T>(value: X) => this = value => {
        let existing: T | undefined;
        for (const element of this.keys()) {
            if (this.compareFn(element, value)) {
                existing = element;
                break;
            }
        }
        if (existing) {
            const { remove } = this.getPriority(existing, value);
            this.delete(remove);
        }
        super.add(value);
        return this;
    };

    /**
     * Tries to remove the item from this set. Returns whether the item is deleted (if the item was found).
     * @param value
     */
    override delete: <X extends T>(value: X) => boolean = value => {
        for (const element of this.keys()) {
            if (this.compareFn(element, value)) {
                return super.delete(element);
            }
        }
        return false;
    };

    /**
     * Returns whether the item exists in the set.
     * @param value
     */
    override has: <X extends T>(value: X) => boolean = value => {
        for (const element of this.keys()) {
            if (this.compareFn(element, value)) {
                return super.has(element);
            }
        }
        return false;
    };

    /**
     * Returns `true` if all items in the collection exist in this set.
     * @param values
     */
    hasAll: <X extends T>(values: readonly X[]) => boolean = values => {
        let hasElements = true;
        for (const element of values) {
            hasElements &&= this.has(element);
            if (!hasElements) {
                break;
            }
        }
        return hasElements;
    };

    /**
     * Adds all elements of the collection to this set.
     * @param values
     * @returns the set itself, for FP purpose
     */
    addAll: <X extends T>(values: readonly X[]) => this = values => {
        for (const element of values) {
            this.add(element);
        }
        return this;
    };

    /**
     * Deletes all elements in the collection from this set. Returns whether this set is changed by the operation (if at
     * least one item is deleted)
     * @param values
     */
    deleteAll: <X extends T>(values: readonly X[]) => boolean = values => {
        let deleted = false;
        for (const element of values) {
            deleted = this.delete(element) || deleted;
        }
        return deleted;
    };

    /**
     * Same as delete, with different return type.
     * @param value
     * @see GeneralSet.delete
     * @returns the set itself, for FP purpose
     */
    remove: <X extends T>(value: X) => this = value => {
        this.delete(value);
        return this;
    };

    /**
     * Same as deleteAll, with different return type.
     * @param values
     * @returns the set itself, for FP purpose
     * @see GeneralSet.deleteAll
     */
    removeAll: <X extends T>(values: readonly X[]) => this = values => {
        this.deleteAll(values);
        return this;
    };

    /**
     * Returns a new General set, with elements passing the specified `predicate`.
     * @param predicate
     */
    filter = (predicate: (value: T, set: GeneralSet<T>) => boolean) => {
        const result = this.clone();
        for (const item of this.keys()) {
            if (!predicate(item, this.clone())) {
                result.delete(item);
            }
        }
        return result;
    };

    /**
     * Returns `true` if at least one of the elements of this `Set` passes the `predicate`.
     * @param predicate
     */
    some = (predicate: (value: T, set: GeneralSet<T>) => boolean) => {
        for (const item of this.keys()) {
            if (predicate(item, this.clone())) {
                return true;
            }
        }
        return false;
    };

    /**
     * Returns `true` if all elements of this `Set` pass the `predicate`.
     * @param predicate
     */
    every = (predicate: (value: T, set: GeneralSet<T>) => boolean) => {
        for (const item of this.keys()) {
            if (!predicate(item, this.clone())) {
                return false;
            }
        }
        return true;
    };

    /**
     * Finds an element passing the specified `predicate` **UNORDERED**, or `undefined` if no element was found.
     * @param predicate
     */
    find = (predicate: (value: T, set: GeneralSet<T>) => boolean) => {
        for (const item of this.keys()) {
            if (predicate(item, this.clone())) {
                return item;
            }
        }
        return undefined;
    };

    /**
     * Returns a new `General Set` with this `Set` elements mapped using specified mapper.
     * @param callbackFn
     */
    map = <U extends Object>(
        callbackFn: (value: T, set: GeneralSet<T>) => U
    ) => {
        const result = new GeneralSet<U>();
        for (const item of this.keys()) {
            result.add(callbackFn(item, this.clone()));
        }
        return result;
    };

    /**
     * Returns a new Array with all elements of this `Set` **UNORDERED**.
     */
    toArray = () => Array.from(this.keys());

    /**
     * Returns a string containing all elements of this `Set`, joined with specified `separator` **UNORDERED**.
     * @param separator
     * @param mapper
     */
    join = (
        separator = ',',
        mapper: (item: T) => string = item => item.toString()
    ) => this.toArray().map(mapper).join(separator);

    /**
     * Returns a new General Set, with elements of this `Set` and compare function of this `Set` set.
     */
    clone: () => GeneralSet<T> = () => {
        const cloned = new GeneralSet(this.values());
        cloned.prioritize = this.prioritize;
        cloned.compareFn = this.compareFn;
        return cloned;
    };

    clean: () => this = () => {
        this.clear();
        return this;
    };
}
